'use client'

import DOMPurify from 'dompurify'
import * as LucideIcons from 'lucide-react'
import React from 'react'

import { iconsMap } from '../../../constants/icons'
import { cn } from '../../../utils/cn'

import type { IProgramItem } from '../_types/types'
import type { MEDANTE_ICONS_ENUM } from '@/types'

export interface IProgramCardContentItemsProps {
	items: IProgramItem[]
}

/**
 * Detect if item should be highlighted (explicit flag or content starts with "+ ")
 */
function isHighlightedItem(item: IProgramItem): boolean {
	if (item.highlighted === true) return true
	const content = typeof item.content === 'string' ? item.content.trim() : ''
	return content.startsWith('+ ')
}

/**
 * Get content for display; strip "+ " prefix when used as highlight marker
 */
function getDisplayContent(content: string): string {
	const trimmed = content?.trim() ?? ''
	return trimmed.startsWith('+ ') ? trimmed.substring(2) : (content ?? '')
}

const HTML_ENTITY_DECODE: Record<string, string> = {
	'&lt;': '<',
	'&gt;': '>',
	'&quot;': '"',
	'&#34;': '"',
	'&#39;': "'",
	'&apos;': "'",
	'&amp;': '&',
	'&nbsp;': '\u00A0'
}

/**
 * Decode HTML entities (e.g. &lt; &gt; &quot;) so tags are real HTML for sanitizer and rendering.
 * Handles single and double encoding; works in browser and Node (SSR).
 */
function decodeHtmlEntities(html: string): string {
	if (!html?.trim()) return html ?? ''
	let decoded = html
	let prev = ''
	// Loop until stable to handle double-encoded content (e.g. &amp;lt; → &lt; → <)
	while (prev !== decoded) {
		prev = decoded
		decoded = Object.entries(HTML_ENTITY_DECODE).reduce(
			(str, [entity, char]) => str.split(entity).join(char),
			decoded
		)
		// Numeric entities (e.g. &#60; &#62;)
		decoded = decoded.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
		decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
			String.fromCharCode(parseInt(hex, 16))
		)
	}
	return decoded
}

/** Check if list item content is "extra" (starts with "+ ") */
function isExtraListItem(liContent: string): boolean {
	return liContent?.trim()?.startsWith('+ ') ?? false
}

/** Strip "+ " from start of list item for display */
function stripExtraPrefix(liContent: string): string {
	const trimmed = liContent?.trim() ?? ''
	return trimmed.startsWith('+ ') ? trimmed.substring(2) : (liContent ?? '')
}

/** Check if paragraph content should be highlighted (starts with "+ ") */
function isHighlightedParagraph(innerHTML: string): boolean {
	return innerHTML?.trim()?.startsWith('+ ') ?? false
}

/** Strip "+ " from start of paragraph content for display */
function stripParagraphHighlightPrefix(innerHTML: string): string {
	const trimmed = innerHTML?.trim() ?? ''
	return trimmed.startsWith('+ ') ? trimmed.substring(2) : (innerHTML ?? '')
}

export type ContentSegment =
	| { type: 'html'; html: string }
	| { type: 'listGroups'; groups: { isExtra: boolean; items: string[] }[] }

/**
 * Parse HTML content: split single <ul> into multiple <ul> by grouping consecutive
 * list items by "+ " prefix. Standard items in one ul, "+ " items in another (highlight on whole ul).
 */
function parseContentToSegments(html: string): ContentSegment[] | null {
	if (typeof document === 'undefined' || !html?.trim()) return null
	try {
		const doc = new DOMParser().parseFromString(html, 'text/html')
		const body = doc.body
		if (!body || !body.children.length) return null

		const segments: ContentSegment[] = []

		for (let i = 0; i < body.children.length; i++) {
			const el = body.children[i]
			if (el.tagName === 'UL') {
				const lis = Array.from(el.querySelectorAll(':scope > li'))
				const liContents = lis.map(li => li.innerHTML.trim())
				if (liContents.length === 0) {
					segments.push({ type: 'html', html: el.outerHTML })
					continue
				}
				// Group consecutive items by isExtra
				const groups: { isExtra: boolean; items: string[] }[] = []
				let current: { isExtra: boolean; items: string[] } | null = null

				for (const content of liContents) {
					const extra = isExtraListItem(content)
					if (current && current.isExtra === extra) {
						current.items.push(content)
					} else {
						current = { isExtra: extra, items: [content] }
						groups.push(current)
					}
				}

				segments.push({ type: 'listGroups', groups })
			} else if (el.tagName === 'P') {
				const inner = el.innerHTML ?? ''
				if (isHighlightedParagraph(inner)) {
					const stripped = stripParagraphHighlightPrefix(inner)
					segments.push({
						type: 'html',
						html: `<p class="m-program-card__p--highlighted">${stripped}</p>`
					})
				} else {
					segments.push({ type: 'html', html: el.outerHTML })
				}
			} else {
				segments.push({ type: 'html', html: el.outerHTML })
			}
		}

		return segments.length > 0 ? segments : null
	} catch {
		return null
	}
}

/**
 * Sort items within a column by sortOrder (lower first; missing sortOrder last)
 */
function sortBySortOrder<T extends { sortOrder?: number }>(list: T[]): T[] {
	return [...list].sort((a, b) => (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity))
}

/**
 * Check if icon is a MEDANTE icon (from constants/icons)
 */
function isMedanteIcon(icon?: string): icon is MEDANTE_ICONS_ENUM {
	return !!icon && icon in iconsMap
}

/**
 * Get Lucide icon component by name
 */
const getIconComponent = (iconName?: string) => {
	if (!iconName) return LucideIcons.Check

	// Try to find the icon in lucide-react
	const IconComponent = (LucideIcons as any)[iconName]
	return IconComponent || LucideIcons.Check
}

function renderContent(sanitizedContent: string) {
	const segments = parseContentToSegments(sanitizedContent)

	if (!segments || segments.length === 0) {
		return (
			<div
				dangerouslySetInnerHTML={{ __html: sanitizedContent }}
				className='m-program-card__content-html'
			/>
		)
	}

	return (
		<>
			{segments.map((seg, segIndex) => {
				if (seg.type === 'html') {
					return (
						<div
							key={`html-${segIndex}`}
							dangerouslySetInnerHTML={{ __html: seg.html }}
						/>
					)
				}
				return (
					<React.Fragment key={`list-${segIndex}`}>
						{seg.groups.map((group, groupIndex) => {
							const listContent = group.items
								.map(
									itemContent =>
										`<li>${DOMPurify.sanitize(
											group.isExtra
												? stripExtraPrefix(itemContent)
												: itemContent
										)}</li>`
								)
								.join('')
							return (
								<ul
									key={`${segIndex}-${groupIndex}`}
									className={cn(
										'm-program-card__list',
										group.isExtra && 'm-program-card__list--highlighted'
									)}
									dangerouslySetInnerHTML={{ __html: listContent }}
								/>
							)
						})}
					</React.Fragment>
				)
			})}
		</>
	)
}

function renderItem(item: IProgramItem, index: number) {
	const rawContent = getDisplayContent(item.content)
	const decodedContent = decodeHtmlEntities(rawContent)
	const sanitizedContent =
		typeof window !== 'undefined'
			? DOMPurify.sanitize(decodedContent, {
					ALLOWED_TAGS: ['ul', 'li', 'b', 'strong', 'p', 'a'],
					ALLOWED_ATTR: ['href']
				})
			: decodedContent
	const useMedanteIcon = isMedanteIcon(item.icon)
	const IconComponent = useMedanteIcon ? null : getIconComponent(item.icon)
	const isHighlighted = isHighlightedItem(item)

	return (
		<div
			key={index}
			className={cn(
				'm-program-card__item',
				isHighlighted && 'm-program-card__item--highlighted'
			)}
		>
			<div className='m-program-card__item-header'>
				<div className='m-program-card__item-icon'>
					{useMedanteIcon ? (
						<img
							src={iconsMap[item.icon as MEDANTE_ICONS_ENUM]}
							alt=''
							className='m-program-card__item-icon-img'
							aria-hidden
						/>
					) : (
						IconComponent && <IconComponent size={24} />
					)}
				</div>

				<div className='m-program-card__item-title'>{item.title}</div>
			</div>
			<div className='m-program-card__item-content'>{renderContent(sanitizedContent)}</div>
		</div>
	)
}

export const ProgramCardContentItems: React.FC<IProgramCardContentItemsProps> = ({ items }) => {
	const col1 = sortBySortOrder(items.filter(i => (i.column ?? 1) === 1))
	const col2 = sortBySortOrder(items.filter(i => i.column === 2))
	const col3 = sortBySortOrder(items.filter(i => i.column === 3))
	const hasColumns = col2.length > 0 || col3.length > 0

	if (!hasColumns) {
		const sorted = sortBySortOrder(items)
		return (
			<div className='m-program-card__items'>
				{sorted.map((item, index) => renderItem(item, index))}
			</div>
		)
	}

	return (
		<div className='m-program-card__items m-program-card__items--columns'>
			<div className='m-program-card__items-row'>
				<div className='m-program-card__items-col m-program-card__items-col--1'>
					<div className='m-program-card__items-inner'>
						{col1.map((item, index) => renderItem(item, index))}
					</div>
				</div>
				<div className='m-program-card__items-col m-program-card__items-col--2'>
					<div className='m-program-card__items-inner'>
						{col2.map((item, index) => renderItem(item, index))}
					</div>
				</div>
			</div>
			{col3.length > 0 && (
				<div className='m-program-card__items-col m-program-card__items-col--3 m-program-card__items-col--full'>
					<div className='m-program-card__items-inner'>
						{col3.map((item, index) => renderItem(item, index))}
					</div>
				</div>
			)}
		</div>
	)
}

ProgramCardContentItems.displayName = 'AProgramCardContentItems'
